import { createSlice, createSelector } from '@reduxjs/toolkit';
import _ from "lodash";
import {getRandomColor} from "../../utils/colorUtils";

const defaultSmaConfig = {
    name: '',
    cutoffWeight: 80,
    minWeightTolerance: 0.0,
    minSectorTolerance: 0.02,
    maxPerHolding: 0,
    blacklistId: null,
    screenerId: null,
    fillType: "prorata",
    etfs: {},  // Ensure this is initialized
};

const initialState = {
    smaConfigs: {},
    currentSmaConfigId: null,
    mode: 'simple',
    smaSettings: {
        sortEtfs: false,
    }
}

const smaSlice = createSlice({
  name: 'smaSlice',
  initialState,
  reducers: {

      updateSmaSettings(state, action) {
          state.smaSettings = _.merge({}, state.smaSettings, action.payload);
      },

      setSmaMode(state, action) {
          state.mode = action.payload;
      },
      setSmaConfig(state, action) {
          const { smaConfig, smaConfigId } = action.payload;
          state.smaConfigs[smaConfigId] = smaConfig;
          state.currentSmaConfigId = smaConfigId;
      },

      updateSmaConfigInstance(state, action) {
          const { smaConfigId, updatedConfig } = action.payload;
          state.smaConfigs[smaConfigId] = _.merge({}, state.smaConfigs[smaConfigId], updatedConfig);
      },

      addEtfToSmaConfig(state, action) {
          const { smaConfigId, etfId } = action.payload;
          const etfWeight = action.payload.etfWeight || 0;

          // Ensure the etfs property is initialized before adding to it
          if (!state.smaConfigs[smaConfigId].etfs) {
              state.smaConfigs[smaConfigId].etfs = {};
          }
         if (!state.smaConfigs[smaConfigId].etfs[etfId]) {
              // Assign a random color if it's a new ETF
              const color = getRandomColor();

              // Add the ETF with weight and color
              state.smaConfigs[smaConfigId].etfs[etfId] = {
                  weight: etfWeight,
                  color: color,
                  doNotReplicate: false
              };
          } else {
              // If ETF exists, just update the weight (if needed)
              state.smaConfigs[smaConfigId].etfs[etfId].weight = etfWeight;
          }
         },

      removeEtfFromSmaConfig(state, action) {
          const { smaConfigId, etfId } = action.payload;
          delete state.smaConfigs[smaConfigId].etfs[etfId];
      },

      // New Reducer: Initialize a new SMA Config with defaults
      initializeNewSmaConfig(state, action) {
          const { smaConfigId } = action.payload || {};
          const newSmaConfigId = smaConfigId || _.uniqueId('sma_');  // Auto-generate ID if none provided

          // Clone default config to ensure etfs is initialized
          state.smaConfigs[newSmaConfigId] = _.cloneDeep(defaultSmaConfig);
          state.currentSmaConfigId = newSmaConfigId;
      },

      initializeSmaWithTemplate(state, action) {
          const { smaConfigId, etfs } = action.payload;
          const newSmaConfigId = smaConfigId || _.uniqueId('sma_');
          const newSmaConfig = _.cloneDeep(defaultSmaConfig);

          etfs.forEach(({ symbol, weight }) => {
              const color = getRandomColor();
              newSmaConfig.etfs[symbol] = {
                  weight: weight || 0,
                  color: color,
                  doNotReplicate: false
              };
          });

          state.smaConfigs[newSmaConfigId] = newSmaConfig;
          state.currentSmaConfigId = newSmaConfigId;
      },
      ensureDefaultSmaConfig(state) {
          if (!state.currentSmaConfigId) {
              // Initialize a new config if there isn't one
              const newSmaConfigId = _.uniqueId('sma_');
              state.smaConfigs[newSmaConfigId] = _.cloneDeep(defaultSmaConfig);
              state.currentSmaConfigId = newSmaConfigId;
          }
      },

      switchSmaConfig(state, action) {
          const { smaConfigId } = action.payload;
          if (state.smaConfigs[smaConfigId]) {
              state.currentSmaConfigId = smaConfigId;
              state.lastSavedSmaConfigId = smaConfigId;
          }
      },
      deleteSmaConfig(state, action) {
          const { smaConfigId } = action.payload;
          if (state.smaConfigs[smaConfigId]) {
            delete state.smaConfigs[smaConfigId];

            // If the deleted config was the current one, reset currentSmaConfigId
            if (state.currentSmaConfigId === smaConfigId) {
              state.currentSmaConfigId = null;
            }
      }
    },
  }
});

export const {
    setSmaConfig,
    updateSmaSettings,
    ensureDefaultSmaConfig,
    updateSmaConfigInstance,
    addEtfToSmaConfig,
    removeEtfFromSmaConfig,
    initializeNewSmaConfig,
    initializeSmaWithTemplate,
    switchSmaConfig,
    deleteSmaConfig,
    setSmaMode
} = smaSlice.actions;

export default smaSlice.reducer;


export const selectAllConfigs = createSelector(
  state => state.sma.smaConfigs,  // Access the smaConfigs from the state
  smaConfigs => {
    return Object.keys(smaConfigs).map(smaConfigId => {
      const config = smaConfigs[smaConfigId];
      const etfs = Object.keys(config.etfs).map(etfId => ({
        symbol: etfId,
        weight: config.etfs[etfId].weight,
        color: config.etfs[etfId].color
      }));

      return {
        id: smaConfigId,
        etfs,
        cutoffWeight: config.cutoffWeight,
        fillType: config.fillType,
        minWeightTolerance: config.minWeightTolerance,
        maxPerHolding: config.maxPerHolding,
          name: config?.name ?? 'Unnammed SMA Config'
      };
    });
  }
);
